/**
 * Loads the recent records and the server-side counts together.
 *
 * The counts come from aggregation queries rather than from the loaded list, so the
 * header stays true even when there are more records than the fetch cap.
 */
import { useCallback, useEffect, useState } from 'react';
import { accountDeletionsService } from '../services/accountDeletionsService';
import type {
  AccountDeletionRecord,
  DeletionHealthCounts,
} from '../types/accountDeletionTypes';

const EMPTY_COUNTS: DeletionHealthCounts = {
  inProgress: 0,
  completed: 0,
  failed: 0,
  stalled: 0,
};

export const useAccountDeletions = () => {
  const [records, setRecords] = useState<AccountDeletionRecord[]>([]);
  const [counts, setCounts] = useState<DeletionHealthCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [loadedRecords, loadedCounts] = await Promise.all([
        accountDeletionsService.getRecent(),
        accountDeletionsService.getHealthCounts(),
      ]);
      setRecords(loadedRecords);
      setCounts(loadedCounts);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load account deletions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { records, counts, loading, error, reload: load };
};
